<template>
  <v-dialog v-model="dialog" max-width="900px" scrollable>
    <v-card v-if="dialog">
      <v-card-title class="headline">
        {{ transition.name || $t('job-manager.transition-details') }}
        <v-spacer></v-spacer>
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <!-- Header / Attempt -->
        <v-row class="mb-2">
          <v-col cols="12" class="d-flex align-center">
            <span class="mr-3 font-weight-medium">
              {{ transition.name || $t('job-manager.transition-details') }}
            </span>
            <attempt-selector
                v-if="executionsCount > 1"
                v-model="selectedAttempt"
                :max-attempts="executionsCount"
            />
            <v-spacer></v-spacer>
            <v-chip small :color="getStatusColor(currentExecStatus)" text-color="white">
              {{ currentExecStatus }}
            </v-chip>
          </v-col>
        </v-row>

        <!-- Metrics + IDs -->
        <v-row class="mb-2">
          <v-col cols="12" md="6">
            <div class="detail-item">
              <div class="detail-label">{{ $t('job-manager.started-at') }}:</div>
              <div class="detail-value">{{ formatDate(currentExec && (currentExec.startedAt || currentExec.createdAt)) || '-' }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">{{ $t('job-manager.finished-at') }}:</div>
              <div class="detail-value">{{ formatDate(currentExec && currentExec.finishedAt) || '-' }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Wait time:</div>
              <div class="detail-value">{{ humanDuration(waitTimeSec) || '-' }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">{{ $t('job-manager.duration') }}:</div>
              <div class="detail-value">{{ humanDuration(durationSec) || '-' }}</div>
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="detail-item">
              <div class="detail-label">{{ $t('job-manager.attempt') }}:</div>
              <div class="detail-value">{{ selectedAttempt }} / {{ executionsCount }}</div>
            </div>

            <div class="detail-item">
              <div class="detail-label">Execution ID:</div>
              <div class="detail-value d-flex align-center">
                <code class="mono">{{ truncateId(currentExec && currentExec.id) }}</code>
                <v-btn icon small @click="copy(currentExec && currentExec.id)" :disabled="!currentExec || !currentExec.id" :title="$t('copy')">
                  <v-icon small>mdi-content-copy</v-icon>
                </v-btn>
              </div>
            </div>

            <div class="detail-item" v-if="currentExec && currentExec.fromExecutionId">
              <div class="detail-label">From Execution ID:</div>
              <div class="detail-value d-flex align-center">
                <code class="mono">{{ truncateId(currentExec.fromExecutionId) }}</code>
                <v-btn icon small @click="copy(currentExec.fromExecutionId)" :title="$t('copy')">
                  <v-icon small>mdi-content-copy</v-icon>
                </v-btn>
              </div>
            </div>

            <div class="detail-item">
              <div class="detail-label">{{ $t('job-manager.transports') }}:</div>
              <div class="detail-value">
                <template v-if="currentExec && Array.isArray(currentExec.transports) && currentExec.transports.length">
                  <v-chip v-for="(t, idx) in currentExec.transports" :key="`t-${idx}`" x-small color="blue lighten-2" text-color="white" class="mr-1">
                    {{ t }}
                  </v-chip>
                </template>
                <template v-else>-</template>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Tabs -->
        <tab-switcher v-model="activeTab" :tabs="tabs">
          <!-- Context -->
          <template v-slot:context>
            <div>
              <h3 class="mb-2">{{ $t('job-manager.input-context') }}</h3>
              <context-viewer :context="currentExec && currentExec.inputContext || {}" />
              <h3 class="mt-4 mb-2">{{ $t('job-manager.output-context') }}</h3>
              <context-viewer :context="currentExec && currentExec.outputContext || {}" />
            </div>
          </template>

          <!-- Logs -->
          <template v-slot:logs>
            <div v-if="currentExec && currentExec.id">
              <log-viewer
                  v-if="isLogsTab && dialog"
                  ref="logViewer"
                  :execution-id="currentExec.id"
                  :key="currentExec.id"
              />
            </div>
            <div v-else class="pa-4 grey--text">
              {{ $t('job-manager.no-execution-selected') }}
            </div>
          </template>

          <!-- Raw (via composant) -->
          <template v-slot:raw>
            <raw-json-panel :value="currentExec" :title="$t('job-manager.raw-json')" />
          </template>
        </tab-switcher>

        <v-card-actions class="mt-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="close" small>{{ $t('job-manager.close') }}</v-btn>
        </v-card-actions>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import ContextViewer from '../elements/ContextViewer.vue';
import LogViewer from '../elements/LogViewer.vue';
import TabSwitcher from '../elements/TabSwitcher.vue';
import AttemptSelector from '../elements/AttemptSelector.vue';
import RawJsonPanel from '../elements/RawJsonPanel.vue';

export default {
  name: 'TransitionDetailModal',
  components: { ContextViewer, LogViewer, TabSwitcher, AttemptSelector, RawJsonPanel },
  props: {
    value: { type: Boolean, default: false },
    transition: { type: Object, default: () => ({ name: null, place: null, executions: [], jobClassName: null, logs: [] }) }
  },
  data() {
    return {
      activeTab: 0,
      selectedAttempt: 1,
      tabs: [
        { id: 'context', label: this.$t('job-manager.context') },
        { id: 'logs',    label: this.$t('job-manager.logs') },
        { id: 'raw',     label: 'Brut' }
      ]
    };
  },
  watch: {
    transition: {
      immediate: true,
      handler() {
        const execs = Array.isArray(this.transition?.executions) ? this.transition.executions : [];
        this.selectedAttempt = execs.length ? execs.length : 1;
      }
    },
    value(val) {
      if (!val) this.$refs.logViewer?.stopAuto?.();
      else {
        const execs = Array.isArray(this.transition?.executions) ? this.transition.executions : [];
        this.selectedAttempt = execs.length ? execs.length : 1;
      }
    },
    activeTab() {
      if (!this.isLogsTab) this.$refs.logViewer?.stopAuto?.();
    },
    selectedAttempt() {
      this.$refs.logViewer?.stopAuto?.();
    }
  },
  computed: {
    dialog: { get() { return this.value; }, set(v) { this.$emit('input', v); } },
    executionsCount() { return (Array.isArray(this.transition?.executions) ? this.transition.executions.length : 0) || 1; },
    currentExec() {
      const execs = Array.isArray(this.transition?.executions) ? this.transition.executions : [];
      if (!execs.length) return null;
      const idx = Math.max(0, Math.min(this.selectedAttempt - 1, execs.length - 1));
      return execs[idx];
    },
    currentExecStatus() { return (this.currentExec?.status || '').toString().toLowerCase(); },
    isLogsTab() {
      const idx = this.tabs.findIndex(t => t.id === 'logs');
      return (typeof this.activeTab === 'number') ? (this.activeTab === idx) : (this.activeTab === 'logs');
    },
    durationSec() {
      const s = this.parseDate(this.currentExec && (this.currentExec.startedAt || this.currentExec.createdAt));
      const f = this.parseDate(this.currentExec && this.currentExec.finishedAt);
      if (!s || !f) return null;
      return Math.max(0, Math.round((f - s) / 1000));
    },
    waitTimeSec() {
      const c = this.parseDate(this.currentExec && this.currentExec.createdAt);
      const s = this.parseDate(this.currentExec && this.currentExec.startedAt);
      if (!c || !s) return null;
      return Math.max(0, Math.round((s - c) / 1000));
    }
  },
  methods: {
    getStatusColor(status) {
      const colors = { pending:'orange', running:'blue', success:'green', failed:'red', cancelled:'grey', awaiting_approval:'purple' };
      return colors[(status || '').toString().toLowerCase()] || 'grey';
    },
    close() { this.$refs.logViewer?.stopAuto?.(); this.dialog = false; },
    parseDate(d) { try { return d ? new Date(d) : null; } catch { return null; } },
    formatDateTime(d) {
      if (!d) return null;
      try {
        const dt = typeof d === 'string' ? new Date(d) : d;
        if (!dt || isNaN(dt.getTime())) return null;
        const pad = (n) => String(n).padStart(2, '0');
        return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())} ${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
      } catch { return null; }
    },
    humanDuration(sec) {
      if (sec == null) return null;
      if (sec < 60) return `${sec}s`;
      const m = Math.floor(sec/60), s = sec%60;
      if (m < 60) return `${m}m ${s}s`;
      const h = Math.floor(m/60), mm = m%60;
      return `${h}h ${mm}m ${s}s`;
    },
    truncateId(id) { const s = (id || '') + ''; return s.length > 16 ? `${s.slice(0,8)}…${s.slice(-6)}` : s; },
    async copy(text) {
      if (!text) return;
      try { await navigator.clipboard.writeText(String(text)); } catch {
        const ta = document.createElement('textarea'); ta.value = String(text);
        document.body.appendChild(ta); ta.select(); try { document.execCommand('copy'); } catch {
          // fallback
          const range = document.createRange(); range.selectNodeContents(ta);
          const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range);
          document.execCommand('copy');
        }
        document.body.removeChild(ta);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.detail-item {
  display: flex; margin-bottom: 10px;
  .detail-label { font-weight: bold; min-width: 140px; }
  .detail-value { flex: 1; display: flex; align-items: center;
    .mono {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 12px; background: #f3f3f3; padding: 2px 6px; border-radius: 4px; margin-right: 6px;
    }
  }
}
</style>
